import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Shield, Save, KeyRound, Users } from "lucide-react";
import { adminAxios, relativeTime } from "./adminApi";

export default function AdminSettings() {
  const [me, setMe] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [meR, adR] = await Promise.all([
          adminAxios().get("/me"),
          adminAxios().get("/admins"),
        ]);
        setMe(meR.data.admin);
        setAdmins(adR.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally { setLoading(false); }
    })();
  }, []);

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== newPw2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await adminAxios().post("/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setNewPw2("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading settings…</div>;
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Settings</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Admin settings</h1>
        <p className="text-sm text-lm-text2 mt-1">Your account & admin roster.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile */}
        <div className="rounded-lg border border-lm-border bg-lm-surface p-5" data-testid="admin-profile-card">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-lm-primary" />
            <h2 className="font-display font-semibold text-lm-text">Profile</h2>
          </div>
          <div className="space-y-2 text-sm">
            <Row label="Email" value={me?.email} />
            <Row label="Name" value={me?.name || "—"} />
            <Row label="Role" value={me?.role || "admin"} />
            <Row label="Created" value={me?.created_at ? new Date(me.created_at).toLocaleString() : "—"} />
            <Row label="Last login" value={me?.last_login_at ? relativeTime(me.last_login_at) : "—"} />
          </div>
        </div>

        {/* Change password */}
        <form onSubmit={changePw} className="rounded-lg border border-lm-border bg-lm-surface p-5" data-testid="admin-change-pw-card">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-lm-primary" />
            <h2 className="font-display font-semibold text-lm-text">Change password</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1">Current password</label>
              <input
                type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="admin-current-pw"
                required
                className="w-full border border-lm-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 bg-lm-bg"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1">New password</label>
              <input
                type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                data-testid="admin-new-pw"
                minLength={8} required
                className="w-full border border-lm-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 bg-lm-bg"
              />
              <p className="text-[11px] text-lm-text3 font-mono mt-1">Minimum 8 characters.</p>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1">Confirm new password</label>
              <input
                type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                data-testid="admin-new-pw-confirm"
                minLength={8} required
                className="w-full border border-lm-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 bg-lm-bg"
              />
            </div>
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="admin-change-pw-submit"
            className="mt-4 inline-flex items-center gap-2 bg-lm-primary text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-lm-primaryHover disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Update password
          </button>
        </form>

        {/* Admin roster */}
        <div className="lg:col-span-2 rounded-lg border border-lm-border bg-lm-surface p-5" data-testid="admin-roster-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-lm-primary" />
            <h2 className="font-display font-semibold text-lm-text">Admins ({admins.length})</h2>
          </div>
          <div className="bg-lm-bg rounded-lg border border-lm-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-lm-tint/60 text-lm-text3">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Role</th>
                  <th className="text-right px-3 py-2 font-medium">Created</th>
                  <th className="text-right px-3 py-2 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-lm-text3">No admins.</td></tr>
                ) : admins.map((a) => (
                  <tr key={a.admin_id} className="border-t border-lm-border/60">
                    <td className="px-3 py-2">{a.email}{a.email === me?.email && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-lm-primary bg-lm-tint2 px-1.5 py-0.5 rounded">You</span>}</td>
                    <td className="px-3 py-2 text-lm-text2">{a.name || "—"}</td>
                    <td className="px-3 py-2 text-[11px] font-mono uppercase tracking-widest">{a.role || "admin"}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono text-lm-text3">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono text-lm-text3">{relativeTime(a.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-lm-text3 mt-3">Admin invitations happen server-side via `ADMIN_EMAIL` / `ADMIN_PASSWORD` env vars. Contact ops to add a new admin.</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-3 py-1 border-b border-lm-border/40 last:border-b-0">
      <div className="w-32 text-[11px] font-mono uppercase tracking-widest text-lm-text3">{label}</div>
      <div className="flex-1 text-lm-text truncate">{value}</div>
    </div>
  );
}
